import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { readRedirectTo } from "@/features/auth/utils/post-auth-redirect";
import { ContractorOnboardingFlow } from "@/features/onboarding/components/contractor-onboarding-flow";
import { useLeaveOnceOnboarded } from "@/features/onboarding/hooks/use-leave-once-onboarded";
import { useOnboardingPrefill } from "@/features/onboarding/hooks/use-onboarding-prefill";
import {
	BackToRoleButton,
	RolesLoadRetry,
	useOnboardingExit,
} from "@/features/roles/components/back-to-role";
import { analytics } from "@/services/analytics.service";

export function ContractorOnboardingPage() {
	const navigate = useNavigate();
	const location = useLocation();
	// Set when a route guard diverted the visitor here mid-journey; the
	// page they were heading for outranks this role's landing page.
	const redirectTo = readRedirectTo(location.state);
	const markCompletedHere = useLeaveOnceOnboarded(redirectTo ?? "/dashboard");
	const exit = useOnboardingExit(() =>
		navigate("/onboarding", {
			state: {
				allowSelection: true,
				...(redirectTo ? { redirectTo } : {}),
			},
		}),
	);

	// Only an account adding a role has details to carry over.
	const prefill = useOnboardingPrefill(exit.backTo !== null);

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-dashboard-warm-350 px-4 py-8">
			<div className="w-full max-w-2xl">
				<ContractorOnboardingFlow
					onExit={exit.onExit}
					exitControl={<RolesLoadRetry exit={exit} />}
					backToRole={
						exit.backTo ? (
							<BackToRoleButton role={exit.backTo} variant="panel" />
						) : undefined
					}
					prefill={prefill}
					onProfileCreated={() => {
						markCompletedHere();
						analytics.profile.contractorCompleted();
					}}
					onAlreadyExists={() => {
						// A 409 means the server holds a non-placeholder profile, which
						// is exactly what `profileCompleted` means. Without this the
						// store keeps saying "set up your profile" and the guard sends
						// the contractor straight back here.
						useAuthStore.getState().markProfileComplete();
						navigate(redirectTo ?? "/dashboard");
					}}
					onFinished={() => navigate(redirectTo ?? "/dashboard")}
				/>
			</div>
		</div>
	);
}
